import { useEffect, useState, type ReactNode } from "react";
import { Info, X, ChevronLeft, ChevronRight, CheckCircle2, Sparkles } from "lucide-react";

// -------------------------------------------------------------- Tooltip
/**
 * Lightweight zero-dep tooltip + info indicator. Hover or focus the trigger
 * (or the small `(i)` icon when `asIndicator`) to see grounded help text.
 */
export function Tooltip({
  text, children, side = "top", asIndicator = false,
}: { text: ReactNode; children?: ReactNode; side?: "top" | "right" | "bottom" | "left"; asIndicator?: boolean }) {
  const [open, setOpen] = useState(false);
  const pos = {
    top:    "bottom-full left-1/2 -translate-x-1/2 mb-1.5",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-1.5",
    left:   "right-full top-1/2 -translate-y-1/2 mr-1.5",
    right:  "left-full top-1/2 -translate-y-1/2 ml-1.5",
  }[side];
  const trigger = asIndicator
    ? <Info size={13} className="inline-block text-muted hover:text-accent cursor-help" />
    : children;
  return (
    <span
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={asIndicator ? 0 : -1}
    >
      {trigger}
      {open && (
        <span
          role="tooltip"
          className={`absolute z-50 ${pos} max-w-xs whitespace-normal rounded-md border border-border bg-panel px-2.5 py-1.5 text-xs text-text shadow-lg`}
        >
          {text}
        </span>
      )}
    </span>
  );
}

/** `Foo (i)` shorthand: a label with an inline info indicator. */
export function HelpLabel({ label, help, className = "" }: { label: ReactNode; help: ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      {label}
      <Tooltip text={help} asIndicator />
    </span>
  );
}

// -------------------------------------------------------------- Wizard
export interface WizardStep {
  title: string;
  blurb: ReactNode;
  cta?: { label: string; to: string };
  doc?: string;        // anchor in /welcome page
}

const STORAGE_KEY = "ckos.wizard.dismissed";

const STEPS: WizardStep[] = [
  {
    title: "1. Pick a workspace",
    blurb: <>Switch tenants/workspaces from the header dropdown. Catalog, ontology and agents are scoped to the workspace you choose.</>,
    cta: { label: "Go Workspaces", to: "/workspaces" },
  },
  {
    title: "2. Inspect the ontology",
    blurb: <>The ontology is the semantic kernel — entities, relations, taxonomies. Acme&apos;s v1.0.0 uses Ontoligent-authored business types; external mappings are separate, versioned, and rights-gated.</>,
    cta: { label: "Go Catalog", to: "/catalog" },
  },
  {
    title: "3. Connect a source",
    blurb: <>Add an Upload, S3, MCP server, web crawl, or DB connector. Each ingestion run is content-addressed, ACL-tagged, and lineage-tracked.</>,
    cta: { label: "Go Catalog", to: "/catalog" },
  },
  {
    title: "4. Build a pipeline",
    blurb: <>A Pipeline is a DAG of processors (parse → chunk → embed → extract → graph write → govern). The Acme demo ships <code>acme-ingest-default</code>.</>,
    cta: { label: "Go Pipelines", to: "/pipelines" },
  },
  {
    title: "5. Tune retrieval",
    blurb: <>Retrieval Profiles compose vector + BM25 + graph traversal with policies (recency, source trust) and a reranker. Profile <code>acme-hybrid-default</code> is preconfigured.</>,
    cta: { label: "Go Retrieval", to: "/retrieval" },
  },
  {
    title: "6. Run an agent",
    blurb: <>Six reference agents are seeded: Underwriting, Claims Triage, Regulatory QA, Counterparty Exposure, Renewals, and Loss Reserves. Each cites the chunks it used.</>,
    cta: { label: "Go Agents", to: "/agents" },
  },
  {
    title: "7. Gate with Evals + Governance",
    blurb: <>Eval datasets (one per use case) gate every ontology, retrieval, and agent change. Governance shows lineage, audit, cost and access decisions.</>,
    cta: { label: "Go Evals", to: "/evals" },
  },
];

/**
 * Floating "How to use" wizard. Auto-opens for first-time users; can be
 * re-opened from `WizardLauncher`.
 */
export function Wizard({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [i, setI] = useState(0);
  useEffect(() => { if (!open) setI(0); }, [open]);
  if (!open) return null;
  const step = STEPS[i];
  const last = i === STEPS.length - 1;
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-20 sm:pt-24">
      <button
        type="button"
        aria-label="Close getting started wizard"
        className="absolute inset-0 z-0 bg-black/70 "
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative z-10 my-auto w-full max-w-lg rounded-xl border border-border bg-panel p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted">
            <Sparkles size={14} className="text-accent" /> Getting started · step {i + 1} of {STEPS.length}
          </div>
          <button className="btn px-2 py-1" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </div>
        <h2 className="mb-2 text-lg font-semibold">{step.title}</h2>
        <p className="text-sm leading-relaxed text-muted">{step.blurb}</p>
        <div className="mt-4 flex items-center justify-between">
          <div className="flex gap-0">
            {STEPS.map((_, k) => (
              <span key={k}
                className={`h-1.5 w-6 rounded-full ${k <= i ? "bg-accent" : "bg-border"}`} />
            ))}
          </div>
          <div className="flex items-center gap-0.5">
            <button className="btn" disabled={i === 0} onClick={() => setI(i - 1)}>
              <ChevronLeft size={14} /> Back
            </button>
            {step.cta && (
              <a href={step.cta.to} className="btn">{step.cta.label}</a>
            )}
            {last ? (
              <button
                className="btn-primary"
                onClick={() => { localStorage.setItem(STORAGE_KEY, "1"); onClose(); }}
              >
                <CheckCircle2 size={14} /> Finish
              </button>
            ) : (
              <button className="btn-primary" onClick={() => setI(i + 1)}>
                Next <ChevronRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Header button to (re)open the wizard. Pulses on first visit. */
export function WizardLauncher() {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState<boolean>(() => !!localStorage.getItem(STORAGE_KEY));
  // Auto-open once for first-time users; persist 'seen' on any close so
  // a refresh won't re-trigger it (X, backdrop click, or Finish all count).
  useEffect(() => {
    if (!seen) { const t = setTimeout(() => setOpen(true), 600); return () => clearTimeout(t); }
  }, [seen]);
  const close = () => {
    setOpen(false);
    if (!seen) {
      localStorage.setItem(STORAGE_KEY, "1");
      setSeen(true);
    }
  };
  return (
    <>
      <button
        className={`btn ${seen ? "" : "ring-2 ring-accent/50 animate-pulse"}`}
        onClick={() => { setOpen(true); }}
        title="How to use"
      >
        <Sparkles size={14} /> How to use
      </button>
      <Wizard open={open} onClose={close} />
    </>
  );
}
